import type {
  MerchantFeedCheckpoint,
  MerchantFeedPage,
  NotificationPushPublicKey,
  OrderCommandInput,
  RegisterNotificationDeviceInput,
} from "@gobro/contracts";
import type {
  AuthMe,
  MerchantStatement,
  MerchantWorkspace,
  OrderPage,
  Organization,
  Store,
} from "./domain";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: "DELETE" | "GET" | "POST" | "PUT";
}

export class MerchantApi {
  constructor(readonly baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1") {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response: Response;
    try {
      const request: RequestInit = {
        credentials: "include",
        headers: {
          accept: "application/json",
          ...(options.body === undefined ? {} : { "content-type": "application/json" }),
          ...options.headers,
        },
        method: options.method ?? "GET",
      };
      if (options.body !== undefined) request.body = JSON.stringify(options.body);
      response = await fetch(`${this.baseUrl}${path}`, request);
    } catch (error) {
      throw new ApiError(error instanceof Error ? error.message : "Network unavailable", 0);
    }
    const contentType = response.headers.get("content-type") ?? "";
    const data = contentType.includes("json")
      ? ((await response.json()) as Record<string, unknown>)
      : await response.text();
    if (!response.ok) {
      const problem = typeof data === "object" ? data : undefined;
      const detail = problem && typeof problem.detail === "string" ? problem.detail : String(data);
      throw new ApiError(detail || `HTTP ${response.status}`, response.status, problem);
    }
    return data as T;
  }

  me() {
    return this.request<AuthMe>("/auth/me");
  }

  requestOtp(phoneE164: string) {
    return this.request<{ challengeId: string }>("/auth/otp/request", {
      body: { phoneE164, purpose: "sign_in" },
      method: "POST",
    });
  }

  verifyOtp(challengeId: string, code: string) {
    return this.request<{ user: AuthMe }>("/auth/otp/verify", {
      body: { challengeId, clientType: "web", code, deviceName: "GoBro Partenaire PWA" },
      method: "POST",
    });
  }

  organizations() {
    return this.request<{ items: Organization[] } | Organization[]>("/organizations");
  }

  stores(organizationId: string) {
    return this.request<{ items: Store[] } | Store[]>(`/organizations/${organizationId}/stores`);
  }

  workspace(organizationId: string, storeId: string) {
    return this.request<MerchantWorkspace>(
      `/merchant/organizations/${organizationId}/stores/${storeId}/catalog`,
    );
  }

  orders(organizationId: string, storeId: string) {
    return this.request<OrderPage>(
      `/merchant/organizations/${organizationId}/stores/${storeId}/orders?limit=100`,
    );
  }

  feed(organizationId: string, storeId: string, afterCursor: string) {
    return this.request<MerchantFeedPage>(
      `/merchant/organizations/${organizationId}/stores/${storeId}/operations/events?afterCursor=${encodeURIComponent(afterCursor)}&limit=200`,
    );
  }

  checkpoint(organizationId: string, storeId: string, checkpoint: MerchantFeedCheckpoint) {
    return this.request<MerchantFeedCheckpoint>(
      `/merchant/organizations/${organizationId}/stores/${storeId}/operations/events/checkpoint`,
      { body: checkpoint, method: "PUT" },
    );
  }

  orderCommand(
    organizationId: string,
    storeId: string,
    orderId: string,
    command: "accept" | "mark-ready" | "reject" | "start-preparation",
    input: OrderCommandInput,
    idempotencyKey: string,
  ) {
    return this.request(
      `/merchant/organizations/${organizationId}/stores/${storeId}/orders/${orderId}/${command}`,
      { body: input, headers: { "idempotency-key": idempotencyKey }, method: "POST" },
    );
  }

  statement(organizationId: string, storeId: string, from: string, to: string) {
    return this.request<MerchantStatement>(
      `/merchant/organizations/${organizationId}/stores/${storeId}/finance/statement?from=${from}&to=${to}&format=json`,
    );
  }

  notificationPushKey() {
    return this.request<NotificationPushPublicKey>("/notifications/push/public-key");
  }

  registerNotificationDevice(input: RegisterNotificationDeviceInput) {
    return this.request<{ deviceId: string; platform: string }>("/notifications/devices", {
      body: input,
      method: "POST",
    });
  }

  logout() {
    return this.request<void>("/auth/logout", { method: "POST" });
  }
}

export function itemsFrom<T>(value: { items: T[] } | T[]): T[] {
  return Array.isArray(value) ? value : value.items;
}
